import { inject, injectable } from 'inversify';
import { Observable } from 'rxjs';

import { ResponsePipe } from '../utils/request.utils';
import { ApiConfig } from './api-config';
import { LightHttp } from './light-http.service';
import { LoginService } from './login.service';

/**
 * Payments Wave 0 (FIX-PLAN 2026-10-18 W0-12 (2)): `GET /payments/status`. Everything the pricing page
 * and the portal need to decide what to OFFER comes from here, so the UI never offers a purchase the
 * server would refuse. `planState` is the server's word; the web must not derive it from legacy fields.
 */
export interface CheckoutStatus {
    /** FREE | ACTIVE | PAST_DUE | PAUSED | CANCELED_ACCESS_UNTIL_PERIOD_END | PENDING | EXPIRED (server-owned vocabulary). */
    planState: string;
    provider: 'STRIPE' | 'PAYPAL' | null;
    planName: string | null;
    interval: string | null;
    /** ISO-8601 or null; the UI formats it (never a raw Date.toString()). */
    accessUntil: string | null;
    canChangePlan: boolean;
    canCancel: boolean;
    /** A PayPal sub of ours still APPROVED/ACTIVE at PayPal but not activated locally (PA-4). */
    pendingSubscriptionId: string | null;
    /** Why the buttons are blocked, when they are: CROSS_GATEWAY | PAYMENT_UPDATE_REQUIRED | IN_PROGRESS | null. */
    blockedReason: string | null;
    /** Stripe Billing Portal URL when `blockedReason === 'PAYMENT_UPDATE_REQUIRED'`. */
    portalUrl?: string | null;
}

/** W0-4: the preview (`preview: true`, nothing charged) and the applied change share one shape. */
export interface PlanChangeResult {
    preview: boolean;
    planName: string;
    /** Cents charged today (upgrade) — 0 for a downgrade. */
    chargedTodayCents: number;
    /** Cents credited to the customer balance (downgrade) — 0 for an upgrade. */
    creditCents: number;
    currency: string;
    nextChargeCents: number;
    /** ISO-8601. */
    nextChargeDate: string;
    /** True when the change also removed a pending cancellation (cpe). */
    resumesSubscription: boolean;
    /** CR-L6-2: the preview's proration instant (epoch seconds); echoed on commit so the dialog amount == the invoice amount. */
    prorationDate?: number;
    /** CR-L6-2: server-rendered §0 sentence — shown verbatim when present. */
    message?: string;
    planDisplayName?: string;
    subscriptionId?: string;
    priceId?: string;
    packageType?: string;
    invoiceId?: string;
}

/**
 * Post-Stripe-upgrade: the body shape is `{ paymentMethodId, planName }` (not the
 * legacy `{ stripeToken, planName }`) — the backend rejects legacy token bodies with
 * HTTP 409 and a "refresh the page" message (stripe-sdk-upgrade.md §4.2).
 *
 * `payStripe` may return a SCA challenge:
 *   `{ requires_action: true, clientSecret: string, subscriptionId: string }`
 *   Frontend then calls `stripe.confirmCardPayment(clientSecret)` and on success
 *   follows up with [confirmStripe] to finalize on the server.
 * Otherwise it returns `{ state: "success" }`.
 */
@injectable()
export class PaymentService {
    constructor(
        @inject(ApiConfig) private apiConfig: ApiConfig,
        @inject(LightHttp) private http: LightHttp,
        @inject(LoginService) private loginService: LoginService,
    ) { }

    /**
     * W0-1 step 2: `attemptId` is a per-submit UUID the server turns into the Stripe idempotency key
     * `pay-{attemptId}` (flag `payDecideBeforeCharge`; ignored by today's path). Omitted from the body when the
     * caller passes none, so every other caller's request is unchanged.
     */
    payStripe(paymentMethodId: string, planName: string, attemptId?: string): Observable<any> {
        if (!this.loginService.isUserLoggedIn) {
            throw new Error('Only logged in users can make a payment');
        }
        return this.http.post(this.apiConfig.getPaymentsPayEndpoint(), {
            paymentMethodId,
            planName,
            ...(attemptId ? { attemptId } : {}),
        }).pipe(
            ResponsePipe,
        );
    }

    /** Finalize a SCA-challenged subscription after `stripe.confirmCardPayment` succeeds. */
    confirmStripe(subscriptionId: string): Observable<any> {
        if (!this.loginService.isUserLoggedIn) {
            throw new Error('Only logged in users can confirm a payment');
        }
        return this.http.post(this.apiConfig.getPaymentsConfirmStripeEndpoint(), {
            subscriptionId
        }).pipe(
            ResponsePipe,
        );
    }

    payPayPal(subscriptionId: string, planName: string): Observable<any> {
        if (!this.loginService.isUserLoggedIn) {
            throw new Error('Only logged in users can make a payment');
        }
        return this.http.post(this.apiConfig.getPaymentsPayPalEndpoint(), {
            subscriptionId,
            planName
        }).pipe(
            ResponsePipe,
        );
    }

    // ===== Payments Wave 0 (FIX-PLAN 2026-10-18). Routes ship in professorx behind their flags; lane L5 wires the UI. =====

    /** W0-12 (2): `GET /payments/status` (flag `checkoutStatusGate`). */
    getStatus(): Observable<CheckoutStatus> {
        if (!this.loginService.isUserLoggedIn) {
            throw new Error('Only logged in users can read their checkout status');
        }
        return this.http.get<CheckoutStatus>(this.apiConfig.getPaymentsStatusEndpoint()).pipe(
            ResponsePipe,
        );
    }

    /**
     * W0-12 (1): `POST /payments/paypal/create-subscription {planName, attemptId}` → `{subscriptionId}`.
     * `attemptId` is a per-submit UUID (reused only for the network retry of that same submit); the server
     * sends it as `PayPal-Request-Id`, so a replay returns the same subscription instead of a second one.
     */
    createPayPalSubscription(planName: string, attemptId: string): Observable<{subscriptionId: string}> {
        if (!this.loginService.isUserLoggedIn) {
            throw new Error('Only logged in users can make a payment');
        }
        return this.http.post(this.apiConfig.getPaymentsPayPalCreateSubscriptionEndpoint(), {
            planName,
            attemptId
        }).pipe(
            ResponsePipe,
        );
    }

    /**
     * W0-4: `POST /payments/change-plan {planName, preview, attemptId?, prorationDate?}` (flag `planChangeV1`). `preview: true`
     * charges nothing and needs no attemptId. CR-L6-2: the commit sends a per-attempt UUID (idempotency key
     * `plan-change-<attemptId>`, reused only for the network retry of that submit) and echoes the preview's `prorationDate`.
     */
    changePlan(planName: string, preview: boolean, attemptId?: string, prorationDate?: number): Observable<PlanChangeResult> {
        if (!this.loginService.isUserLoggedIn) {
            throw new Error('Only logged in users can change their plan');
        }
        const body: Record<string, unknown> = { planName, preview };
        if (attemptId !== undefined) body.attemptId = attemptId;
        if (prorationDate !== undefined) body.prorationDate = prorationDate;
        return this.http.post(this.apiConfig.getPaymentsChangePlanEndpoint(), body).pipe(
            ResponsePipe,
        );
    }
}
